import { DamageType } from "../damageType";
import { type Direction, cycleDirection, directionDelta, isDiagonal } from "../direction";
import type { ParticleEmitter } from "../interfaces";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { ProjectileWithMaxRange } from "../projectileWithMaxRange";
import { Plant } from "../things/plant";
import type { XY } from "../xy";
import { BlueExplosion } from "../effects";
import { ParticleEffect } from "../effects/particleEffect";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";

const SPLASH_FREEZE_TURNS = 4;

export class CryoShot extends ProjectileWithMaxRange implements ParticleEmitter {
	static key = "CryoShot";

	getParticleType(): ParticleType {
		return ParticleType.ELECTRO_TRAIL;
	}

	constructor(xy: XY, direction: Direction) {
		super(xy, direction);
	}

	getMaxRange(): number {
		return 6;
	}

	onReachingMaxRange(): void {
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.ICE_BURST, 6));
		this.board!.removeAtEndOfTurn(this);
	}

	protected damageTarget(target: PhysicalThingWithHp): number {
		if (target instanceof Mob) {
			if (target.isPermanentlyFrozen()) return 0;
			const iceMultiplier = target.damageMultipliers.get(DamageType.ICE) ?? 1.0;
			if (iceMultiplier <= 0) return 0;
			const fallbackTurns = Math.max(1, Math.floor(SPLASH_FREEZE_TURNS * iceMultiplier));
			target.freeze(-1, fallbackTurns);
			return 0;
		}
		if (target instanceof Plant) {
			return target.applyDamage(DamageType.ICE, STD_BASE_HP, this);
		}
		return 0;
	}

	onHit(_damageDealt: number): void {
		this.board!.removeAtEndOfTurn(this);
		this.board!.addNonPhysicalThing(new Sound(SoundId.SOLIDIFYING, this.xy));
		this.board!.addEffect(new BlueExplosion(this.xy));
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.ICE_BURST, 22));

		const shadowedCells = this.getShadowedCells();
		const neighbors = this.xy.getNeighbors();
		for (const neighborPos of neighbors) {
			if (!this.board!.isInBounds(neighborPos)) continue;
			if (shadowedCells.some((cell) => cell.equals(neighborPos))) continue;

			const things = this.board!.getCell(neighborPos).getThings();
			for (const thing of things) {
				if (!thing.active) continue;

				if (thing instanceof Mob) {
					if (thing.isPermanentlyFrozen()) continue;
					const iceMultiplier = thing.damageMultipliers.get(DamageType.ICE) ?? 1.0;
					if (iceMultiplier <= 0) continue;
					const turns = Math.max(1, Math.floor(SPLASH_FREEZE_TURNS * iceMultiplier));
					thing.freeze(turns);
				} else if (thing instanceof Plant) {
					thing.applyDamage(DamageType.ICE, STD_BASE_HP, this);
				}
			}
		}
	}

	// Cells the hit thing shields from the splash: a cardinal hit shadows the forward
	// cell and the two beside it; a diagonal hit shadows only the forward-diagonal cell.
	private getShadowedCells(): XY[] {
		const dirs = isDiagonal(this.direction)
			? [this.direction]
			: [cycleDirection(this.direction, -1), this.direction, cycleDirection(this.direction, 1)];
		return dirs.map((dir) => this.xy.add(directionDelta(dir)));
	}

	static deserialize(data: any): CryoShot {
		const shot = new CryoShot(deserializeXY(data.xy), data.direction as Direction);
		shot.visualId = data.visualId;
		shot.travelledDistance = data.travelledDistance;
		return shot;
	}
}
